import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import css from './DeliveriesComponent.module.css';
import DeliveryDetailsPopup from './DeliveryDetailsPopup';

const DeliveriesListComponent = ({ deliveries, hoveredDelivery, onDeliveryHover, onDeliveryUpdate }) => {
    const { t } = useTranslation();
    const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const [columnWidths, setColumnWidths] = useState({ 
        address: 2, 
        deliveryInterval: 1, 
        courier: 1, 
        name: 1, 
        debt: 0.5 
    });
    const [resizing, setResizing] = useState(false);
    const [currentColumn, setCurrentColumn] = useState(null);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(0);
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());
    const [editingGroupName, setEditingGroupName] = useState(null);
    const [groupNames, setGroupNames] = useState({});
    const [tempGroupName, setTempGroupName] = useState('');
    const [draggedDelivery, setDraggedDelivery] = useState(null);
    const [dragOverGroup, setDragOverGroup] = useState(null);
    const [dragOverPosition, setDragOverPosition] = useState(null);
    const [showMoveDropdown, setShowMoveDropdown] = useState(null);
    const [moveDropdownPosition, setMoveDropdownPosition] = useState({ x: 0, y: 0 });
    const [showRowMoveDropdown, setShowRowMoveDropdown] = useState(null);
    const [rowMoveDropdownPosition, setRowMoveDropdownPosition] = useState({ x: 0, y: 0 });
    const tableRef = useRef(null);
    const tooltipRef = useRef(null);

    const formatAddress = (delivery) => {
        const parts = [];
        if (delivery.devlieryCity) parts.push(delivery.devlieryCity);
        if (delivery.deliveryStreet) parts.push(delivery.deliveryStreet);
        if (delivery.buidlingNumber) parts.push(delivery.buidlingNumber);
        if (delivery.apartmnetNumber) parts.push(`кв. ${delivery.apartmnetNumber}`);
        return parts.join(', ');
    };

    const handleMouseEnter = (e, content) => {
        if (e.target.scrollWidth > e.target.clientWidth) {
            const rect = e.target.getBoundingClientRect();
            setTooltip({
                show: true,
                content,
                x: rect.left + rect.width / 2,
                y: rect.top - 10
            });
        }
    };

    const handleMouseLeave = () => {
        setTooltip({ show: false, content: '', x: 0, y: 0 });
    };

    const toggleGroup = (courierKey) => {
        const newCollapsedGroups = new Set(collapsedGroups);
        if (newCollapsedGroups.has(courierKey)) {
            newCollapsedGroups.delete(courierKey);
        } else {
            newCollapsedGroups.add(courierKey);
        }
        setCollapsedGroups(newCollapsedGroups);
    };

    const startEditingGroupName = (courierKey, currentName) => {
        setEditingGroupName(courierKey);
        setTempGroupName(groupNames[courierKey] || currentName);
    };

    const saveGroupName = () => {
        if (editingGroupName && tempGroupName.trim()) {
            setGroupNames(prev => ({
                ...prev,
                [editingGroupName]: tempGroupName.trim()
            }));
        }
        setEditingGroupName(null);
        setTempGroupName('');
    };

    const cancelEditingGroupName = () => {
        setEditingGroupName(null);
        setTempGroupName('');
    };

    const handleGroupNameKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveGroupName();
        } else if (e.key === 'Escape') {
            cancelEditingGroupName();
        }
    };

    const getGroupDisplayName = (courierKey, defaultName) => {
        return groupNames[courierKey] || defaultName;
    };

    const startResizing = (e, column) => {
        setResizing(true);
        setCurrentColumn(column);
        setStartX(e.pageX);
        setStartWidth(columnWidths[column]);
    };

    const stopResizing = () => {
        setResizing(false);
        setCurrentColumn(null);
    };

    const resize = (e) => {
        if (resizing && currentColumn) {
            const diff = e.pageX - startX;
            const containerWidth = tableRef.current.offsetWidth;
            const newWidth = Math.max(1, startWidth + (diff / containerWidth) * 10);
            setColumnWidths(prev => ({ ...prev, [currentColumn]: newWidth }));
        }
    };

    useEffect(() => {
        const handleScroll = () => setTooltip({ show: false, content: '', x: 0, y: 0 });
        window.addEventListener('scroll', handleScroll);
        if (resizing) {
            window.addEventListener('mousemove', resize);
            window.addEventListener('mouseup', stopResizing);
        }
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (resizing) {
                window.removeEventListener('mousemove', resize);
                window.removeEventListener('mouseup', stopResizing);
            }
        };
    }, [resizing]);

    // Group and sort deliveries by courier
    const groupedDeliveries = React.useMemo(() => {
        if (!deliveries || deliveries.length === 0) return {};
        
        // Helper function to calculate distance between two points using Haversine formula
        const calculateDistance = (lat1, lng1, lat2, lng2) => {
            const R = 6371; // Earth's radius in kilometers
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLng = (lng2 - lng1) * Math.PI / 180;
            const a = 
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c; // Distance in kilometers
        };

        // Group deliveries by courier
        const groups = {};
        
        deliveries.forEach(delivery => {
            const courierKey = delivery.assignedCourier || 'unassigned';
            if (!groups[courierKey]) {
                groups[courierKey] = [];
            }
            groups[courierKey].push(delivery);
        });

        // Sort deliveries within each group
        Object.keys(groups).forEach(courierKey => {
            groups[courierKey].sort((a, b) => {
                // First check if both have custom order (from drag and drop reordering)
                if (a.customOrder !== undefined && b.customOrder !== undefined) {
                    return a.customOrder - b.customOrder;
                }
                
                // If only one has custom order, prioritize it based on the order value
                if (a.customOrder !== undefined && b.customOrder === undefined) {
                    return -1; // a comes first
                }
                if (a.customOrder === undefined && b.customOrder !== undefined) {
                    return 1; // b comes first
                }
                
                // Default sorting: by delivery interval first, then by distance
                const aInterval = a.deliveryInterval || '';
                const bInterval = b.deliveryInterval || '';
                const intervalCompare = aInterval.localeCompare(bInterval);
                if (intervalCompare !== 0) return intervalCompare;
                
                // Then sort by distance from depot for same interval
                if (a.latitude && a.longitude && b.latitude && b.longitude) {
                    const depotLat = 41.722520;
                    const depotLng = 44.780379;
                    
                    const aDistanceFromDepot = calculateDistance(depotLat, depotLng, a.latitude, a.longitude);
                    const bDistanceFromDepot = calculateDistance(depotLat, depotLng, b.latitude, b.longitude);
                    
                    return aDistanceFromDepot - bDistanceFromDepot;
                }
                
                return 0;
            });
        });

        // Sort groups by courier name (assigned couriers first, then alphabetically)
        const sortedGroups = {};
        const sortedKeys = Object.keys(groups).sort((a, b) => {
            if (a === 'unassigned' && b !== 'unassigned') return 1;
            if (a !== 'unassigned' && b === 'unassigned') return -1;
            return a.localeCompare(b);
        });

        sortedKeys.forEach(key => {
            sortedGroups[key] = groups[key];
        });

        return sortedGroups;
    }, [deliveries]);

    // Drag and Drop handlers
    const handleDragStart = (e, delivery, courierKey) => {
        setDraggedDelivery({ delivery, fromGroup: courierKey });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ''); // Required for Firefox
        
        // Add visual feedback
        e.target.style.opacity = '0.5';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedDelivery(null);
        setDragOverGroup(null);
        setDragOverPosition(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleGroupDragEnter = (e, courierKey) => {
        e.preventDefault();
        setDragOverGroup(courierKey);
    };

    const handleGroupDragLeave = (e) => {
        // Only clear if we're leaving the group container entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverGroup(null);
            setDragOverPosition(null);
        }
    };

    const handleRowDragOver = (e, index) => {
        e.preventDefault();
        setDragOverPosition(index);
    };

    const handleDrop = (e, targetCourierKey, targetIndex = null) => {
        e.preventDefault();
        
        if (!draggedDelivery) return;

        const { delivery, fromGroup } = draggedDelivery;
        
        // Get current position in the group
        const currentGroup = groupedDeliveries[fromGroup];
        const currentIndex = currentGroup ? currentGroup.findIndex(d => d === delivery) : -1;
        
        // Don't do anything if dropping on the exact same position
        if (fromGroup === targetCourierKey && currentIndex === targetIndex) {
            setDraggedDelivery(null);
            setDragOverGroup(null);
            setDragOverPosition(null);
            return;
        }

        // Determine the new courier assignment
        const newCourier = targetCourierKey === 'unassigned' ? null : targetCourierKey;
        
        // Check if this is a courier change or position change
        const isCourierChange = delivery.assignedCourier !== newCourier;
        const isPositionChange = fromGroup === targetCourierKey && targetIndex !== null && currentIndex !== targetIndex;
        
        if (isCourierChange) {
            // Handle courier change (moving between groups)
            console.log(`Moving delivery ${delivery.id} from "${delivery.assignedCourier || 'unassigned'}" to "${newCourier || 'unassigned'}"`);
            
            const updatedDelivery = {
                ...delivery,
                assignedCourier: newCourier
            };

            if (onDeliveryUpdate) {
                onDeliveryUpdate(updatedDelivery);
            }
        } else if (isPositionChange) {
            // Handle position change within the same group
            console.log(`Reordering delivery ${delivery.id} within "${targetCourierKey}" from position ${currentIndex} to ${targetIndex}`);
            
            // Create a reordered delivery list
            const reorderedDelivery = {
                ...delivery,
                tempOrderIndex: targetIndex // Add a temporary index for reordering
            };

            if (onDeliveryUpdate) {
                onDeliveryUpdate(reorderedDelivery, 'reorder', {
                    fromGroup: fromGroup,
                    fromIndex: currentIndex,
                    toIndex: targetIndex
                });
            }
        }

        // Clear drag state
        setDraggedDelivery(null);
        setDragOverGroup(null);
        setDragOverPosition(null);
    };

    const handleGroupDrop = (e, targetCourierKey) => {
        handleDrop(e, targetCourierKey);
    };

    const showMoveOptions = (e, courierKey) => {
        e.stopPropagation();
        const rect = e.target.getBoundingClientRect();
        setMoveDropdownPosition({
            x: rect.left,
            y: rect.bottom + 5
        });
        setShowMoveDropdown(courierKey);
    };

    const hideMoveDropdown = () => {
        setShowMoveDropdown(null);
    };

    const showRowMoveOptions = (e, delivery) => {
        e.stopPropagation();
        const rect = e.target.getBoundingClientRect();
        setRowMoveDropdownPosition({
            x: rect.left,
            y: rect.bottom + 5
        });
        setShowRowMoveDropdown(delivery.id);
    };

    const hideRowMoveDropdown = () => {
        setShowRowMoveDropdown(null);
    };

    const handleSingleMove = (delivery, targetCourierKey) => {
        const newCourier = targetCourierKey === 'unassigned' ? null : targetCourierKey;
        const updatedDelivery = {
            ...delivery,
            assignedCourier: newCourier
        };

        console.log(`Moving single delivery ${delivery.id} to "${targetCourierKey}"`);

        if (onDeliveryUpdate) {
            onDeliveryUpdate(updatedDelivery);
        }

        hideRowMoveDropdown();
    };

    const handleBulkMove = (sourceCourierKey, targetCourierKey) => {
        if (sourceCourierKey === targetCourierKey) {
            hideMoveDropdown();
            return;
        }

        const deliveriesToMove = groupedDeliveries[sourceCourierKey];
        if (!deliveriesToMove || deliveriesToMove.length === 0) {
            hideMoveDropdown();
            return;
        }

        console.log(`Bulk moving ${deliveriesToMove.length} deliveries from "${sourceCourierKey}" to "${targetCourierKey}"`);

        const newCourier = targetCourierKey === 'unassigned' ? null : targetCourierKey;
        const deliveryIds = deliveriesToMove.map(delivery => delivery.id);

        // Use the new bulk-move action
        if (onDeliveryUpdate) {
            onDeliveryUpdate(null, 'bulk-move', {
                deliveryIds: deliveryIds,
                fromCourier: sourceCourierKey,
                toCourier: targetCourierKey,
                newCourier: newCourier
            });
        }

        hideMoveDropdown();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showMoveDropdown && !event.target.closest('.move-dropdown-container')) {
                hideMoveDropdown();
            }
            if (showRowMoveDropdown && !event.target.closest('.row-move-dropdown-container')) {
                hideRowMoveDropdown();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMoveDropdown, showRowMoveDropdown]);

    return (
        <div className={css.tableSection} ref={tableRef}>
            <div className={css.deliveriesCount}>
                {t('total-deliveries')}: {Object.values(groupedDeliveries).reduce((total, group) => total + group.length, 0)}
            </div>
            <div className={css.tableHeader}>
                <div
                    className={css.addressColumn}
                    style={{ flex: columnWidths.address }}
                >
                    {t('address')}
                    <div
                        className={css.columnResizeHandle}
                        onMouseDown={(e) => startResizing(e, 'address')}
                    />
                </div>
                <div
                    className={css.deliveryIntervalColumn}
                    style={{ flex: columnWidths.deliveryInterval }}
                >
                    {t('delivery-interval')}
                    <div
                        className={css.columnResizeHandle}
                        onMouseDown={(e) => startResizing(e, 'deliveryInterval')}
                    />
                </div>
                <div
                    className={css.courierColumn}
                    style={{ flex: columnWidths.courier }}
                >
                    {t('courier')}
                    <div
                        className={css.columnResizeHandle}
                        onMouseDown={(e) => startResizing(e, 'courier')}
                    />
                </div>
                <div
                    className={css.nameColumn}
                    style={{ flex: columnWidths.name }}
                >
                    {t('modal-name-input-new-category')}
                    <div
                        className={css.columnResizeHandle}
                        onMouseDown={(e) => startResizing(e, 'name')}
                    />
                </div>
                <div
                    className={css.debtColumn}
                    style={{ flex: columnWidths.debt }}
                >
                    {t('debt')}
                    <div
                        className={css.columnResizeHandle}
                        onMouseDown={(e) => startResizing(e, 'debt')}
                    />
                </div>
                <div className={css.actionColumn}>
                    {t('Actions')}
                </div>
            </div>

            <div className={css.tableBody}>
                {Object.keys(groupedDeliveries).length > 0 ? (
                    Object.entries(groupedDeliveries).map(([courierKey, deliveriesInGroup]) => (
                        <div 
                            key={courierKey} 
                            className={`${css.courierGroup} ${dragOverGroup === courierKey ? css.dragOver : ''}`}
                            onDragEnter={(e) => handleGroupDragEnter(e, courierKey)}
                            onDragLeave={handleGroupDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleGroupDrop(e, courierKey)}
                        >
                            {/* Group Header */}
                            <div className={css.groupHeader}>
                                <div 
                                    className={css.groupToggle}
                                    onClick={() => toggleGroup(courierKey)}
                                >
                                    {collapsedGroups.has(courierKey) ? '▶' : '▼'}
                                </div>
                                
                                {editingGroupName === courierKey ? (
                                    <input
                                        type="text"
                                        value={tempGroupName}
                                        onChange={(e) => setTempGroupName(e.target.value)}
                                        onKeyDown={handleGroupNameKeyPress}
                                        onBlur={saveGroupName}
                                        className={css.groupNameInput}
                                        autoFocus
                                    />
                                ) : (
                                    <div 
                                        className={css.groupName}
                                        onDoubleClick={() => startEditingGroupName(
                                            courierKey, 
                                            courierKey === 'unassigned' ? t('unassigned-deliveries') : courierKey
                                        )}
                                    >
                                        {getGroupDisplayName(
                                            courierKey, 
                                            courierKey === 'unassigned' ? t('unassigned-deliveries') : courierKey
                                        )}
                                    </div>
                                )}
                                
                                <div className={css.groupStats}>
                                    ({deliveriesInGroup.length} {t('deliveries')})
                                </div>
                                
                                {deliveriesInGroup.length > 0 && (
                                    <button
                                        className={css.moveButton}
                                        onClick={(e) => showMoveOptions(e, courierKey)}
                                        title={t('move-all-deliveries')}
                                    >
                                        {t('move-all')}
                                    </button>
                                )}
                            </div>

                            {/* Group Content */}
                            {!collapsedGroups.has(courierKey) && (
                                <div className={css.groupContent}>
                                    {deliveriesInGroup.map((delivery, index) => (
                                        <div
                                            key={`${courierKey}-${index}`}
                                            className={`${css.tableRow} ${dragOverPosition === index ? css.dragOverRow : ''}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, delivery, courierKey)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => handleRowDragOver(e, index)}
                                            onDrop={(e) => handleDrop(e, courierKey, index)}
                                            onClick={() => setSelectedDelivery(delivery)}
                                            onMouseEnter={() => onDeliveryHover?.(delivery)}
                                            onMouseLeave={() => onDeliveryHover?.(null)}
                                        >
                                            <div className={css.dragHandle}>⋮⋮</div>
                                            {['address', 'deliveryInterval', 'courier', 'name', 'debt'].map(column => (
                                                <div
                                                    key={column}
                                                    className={css[`${column}Column`]}
                                                    style={{ flex: columnWidths[column] }}
                                                    onMouseEnter={(e) =>
                                                        handleMouseEnter(
                                                            e,
                                                            column === 'address'
                                                                ? formatAddress(delivery)
                                                                : column === 'deliveryInterval'
                                                                ? delivery.deliveryInterval
                                                                : column === 'courier'
                                                                ? delivery.assignedCourier || 'Not assigned'
                                                                : delivery[column === 'name' ? 'clientName' : column]
                                                        )
                                                    }
                                                    onMouseLeave={handleMouseLeave}
                                                >
                                                    <div className={css.columnContent}>
                                                        {column === 'address'
                                                            ? formatAddress(delivery)
                                                            : column === 'deliveryInterval'
                                                            ? delivery.deliveryInterval
                                                            : column === 'courier'
                                                            ? delivery.assignedCourier || 'Not assigned'
                                                            : column === 'debt' && delivery.debt > 0
                                                                ? delivery.debt
                                                                : delivery[column === 'name' ? 'clientName' : column]}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className={css.actionColumn}>
                                                <button
                                                    className={css.rowMoveButton}
                                                    onClick={(e) => showRowMoveOptions(e, delivery)}
                                                    title={t('move-delivery-to-courier')}
                                                >
                                                    {t('move')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className={css.noData}>{t('no-data')}</div>
                )}
            </div>

            {tooltip.show && (
                <div
                    className={css.tooltipContainer}
                    ref={tooltipRef}
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translate(-50%, -100%)'
                    }}
                >
                    <div className={css.tooltip}>{tooltip.content}</div>
                </div>
            )}

            {showMoveDropdown && (
                <div
                    className="move-dropdown-container"
                    style={{
                        position: 'fixed',
                        left: moveDropdownPosition.x,
                        top: moveDropdownPosition.y,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        minWidth: '200px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}
                >
                    <div
                        style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#333'
                        }}
                    >
                        {t('select-target-courier')}
                    </div>
                    {Object.keys(groupedDeliveries)
                        .filter(key => key !== showMoveDropdown) // Exclude the source courier
                        .map(courierKey => (
                            <div
                                key={courierKey}
                                className={css.moveOption}
                                onClick={() => handleBulkMove(showMoveDropdown, courierKey)}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f5f5f5',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f0f8ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                }}
                            >
                                {getGroupDisplayName(
                                    courierKey, 
                                    courierKey === 'unassigned' ? t('unassigned-deliveries') : courierKey
                                )}
                                <span style={{ color: '#666', marginLeft: '8px' }}>
                                    ({groupedDeliveries[courierKey].length})
                                </span>
                            </div>
                        ))}
                    
                    {/* Add option to create new courier group if moving from assigned courier */}
                    {showMoveDropdown !== 'unassigned' && (
                        <div
                            className={css.moveOption}
                            onClick={() => handleBulkMove(showMoveDropdown, 'unassigned')}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderTop: '1px solid #ddd',
                                fontStyle: 'italic',
                                color: '#666'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f0f8ff';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                            }}
                        >
                            {t('unassign-deliveries')}
                        </div>
                    )}
                </div>
            )}

            {showRowMoveDropdown && (
                <div
                    className="row-move-dropdown-container"
                    style={{
                        position: 'fixed',
                        left: rowMoveDropdownPosition.x,
                        top: rowMoveDropdownPosition.y,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        minWidth: '200px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}
                >
                    <div
                        style={{
                            padding: '8px 12px',
                            borderBottom: '1px solid #f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            color: '#333'
                        }}
                    >
                        {t('select-target-courier')}
                    </div>
                    {Object.keys(groupedDeliveries).map(courierKey => {
                        const delivery = Object.values(groupedDeliveries)
                            .flat()
                            .find(d => d.id === showRowMoveDropdown);
                        
                        // Don't show current courier as option
                        if ((delivery?.assignedCourier || 'unassigned') === courierKey) {
                            return null;
                        }

                        return (
                            <div
                                key={courierKey}
                                className={css.moveOption}
                                onClick={() => {
                                    const targetDelivery = Object.values(groupedDeliveries)
                                        .flat()
                                        .find(d => d.id === showRowMoveDropdown);
                                    if (targetDelivery) {
                                        handleSingleMove(targetDelivery, courierKey);
                                    }
                                }}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f5f5f5',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f0f8ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                }}
                            >
                                {getGroupDisplayName(
                                    courierKey, 
                                    courierKey === 'unassigned' ? t('unassigned-deliveries') : courierKey
                                )}
                                <span style={{ color: '#666', marginLeft: '8px' }}>
                                    ({groupedDeliveries[courierKey].length})
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedDelivery && (
                <DeliveryDetailsPopup
                    delivery={selectedDelivery}
                    onClose={() => setSelectedDelivery(null)}
                />
            )}
        </div>
    );
};

export default DeliveriesListComponent;
